/**
 * TabErrorBoundary — minimal React class component that catches render
 * errors inside one Overview widget so the rest of the tab keeps working
 * (IA §9.4 "tab-level error boundary").
 *
 * Reset strategy is intentionally simple: a "Retry" button triggers a full
 * page reload. This is acceptable because (a) most render errors during
 * Phase 2 come from upstream type mismatches and reload is a reliable
 * escape hatch, and (b) we don't want to take a dependency on
 * `react-error-boundary` for one component.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Card, CardContent } from '@/components/ui/card';

export interface TabErrorBoundaryProps {
  children: ReactNode;
  /** Display name used in the error log / card title. */
  name?: string;
}

interface State {
  error: Error | null;
}

export class TabErrorBoundary extends Component<TabErrorBoundaryProps, State> {
  public state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `[TabErrorBoundary${this.props.name ? ':' + this.props.name : ''}]`,
      error,
      info.componentStack,
    );
  }

  private handleRetry = (): void => {
    // Simple reset — the underlying React tree is broken; reload the SPA.
    // Phase 3 may replace with QueryClient.invalidate + state reset if the
    // error recovery requirements tighten.
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <Card data-testid="tab-error-boundary">
          <CardContent className="p-4 space-y-3">
            <div className="text-sm font-medium text-red-600">
              {this.props.name
                ? `${this.props.name} failed to render`
                : 'This widget failed to render'}
            </div>
            <div className="text-xs text-muted-foreground">
              {this.state.error.message}
            </div>
            <button
              type="button"
              onClick={this.handleRetry}
              className="text-xs underline text-primary hover:no-underline"
            >
              Retry
            </button>
          </CardContent>
        </Card>
      );
    }
    return this.props.children;
  }
}
